/**
 * filterUpgradePackages — which subscription packages the Upgrade popup offers.
 *
 * WHY THIS FILE EXISTS (QA in-app-marketing pass, 2026-08-01)
 * ----------------------------------------------------------
 * `UpgradePopup.jsx` carried TWO hand-written copies of this rule — one for the
 * plan cards (was: `!required || sub_tier >= required || is_active`) and one for
 * the Compare-Plans table (was: `!required || sub_tier >= required || sub_tier == 0`).
 * The two tails disagree: `is_active` vs `sub_tier === 0`.
 *
 * Today they happen to agree on live staging data only because the Free package
 * (sub_tier 0) is the single `is_active: true` row. The moment any paid package is
 * marked active — Essential (sub_tier 1) is the obvious candidate, it is priced and
 * carries a Stripe product id — the plan cards would offer a package the comparison
 * table silently omits. Two sources of truth for one rule is the defect; this module
 * is the one source.
 *
 * THE RULE: an upgrade popup opened for a required tier offers that tier and every
 * tier above it, plus the Free tier as the "what you have now" reference column
 * -- but ONLY when Free really is what the user has now. With no required tier,
 * nothing is filtered out.
 *
 * T22.764 (T36.8 #12, 2026-10-29): for a user on a paid plan the Free card is
 * not a reference column, it is a "Downgrade to Free" button -- and it was the
 * FIRST card in a dialog titled "Choose the plan to Upgrade" (Premium user, out
 * of AI tokens, "Get more tokens with ProSuite"). A paid plan is held when any
 * package above Free is the active one (`is_active` or `action === "ACTIVE"`,
 * the same test SubscriptionItem uses for "Current Plan").
 *
 * The Free tier is identified by `sub_tier === 0`, not by `is_active`. `is_active`
 * describes whether a package is purchasable at all — an orthogonal concern that
 * must not decide which tiers an upgrade path displays.
 */

import { isActiveItem } from "../../Settings/Subscription/currentPlanDedupe";

/** The Free tier's `sub_tier`. Free is the baseline column for Free users. */
export const FREE_SUB_TIER = 0;

/**
 * True when the user's active package is a paid one (sub_tier above Free).
 * @param {Array} packages packages from `/subscription/packages/`
 */
export const holdsPaidPlan = (packages) =>
  Array.isArray(packages) &&
  packages.some(
    (pkg) => Number(pkg?.sub_tier) > FREE_SUB_TIER && isActiveItem(pkg)
  );

/**
 * @param {Array<{sub_tier: number}>} packages   packages from `/subscription/packages/`
 * @param {number|null|undefined} requiredSubscription  the tier the upsell demands
 * @param {{paidPlanHeld?: boolean}} [options] pass `paidPlanHeld` when the list
 *   carries no active flags (the Compare-Plans packages); defaults to reading
 *   the list itself
 * @returns {Array} the packages the popup should render, original order preserved
 */
export const filterUpgradePackages = (
  packages,
  requiredSubscription,
  { paidPlanHeld = holdsPaidPlan(packages) } = {}
) => {
  if (!Array.isArray(packages)) return [];
  if (!requiredSubscription) return packages;

  return packages.filter(
    (pkg) =>
      pkg.sub_tier >= requiredSubscription ||
      (pkg.sub_tier === FREE_SUB_TIER && !paidPlanHeld)
  );
};

export default filterUpgradePackages;
